import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { ArrowLeft, FileCheck2, FileWarning, Pencil, Save, Search, User as UserIcon } from 'lucide-react';
import {
  CreateProcedureSchema,
  ENTIDADES_CATALOG,
  PROCEDURES_CATALOG,
  RoomSchema,
  findCatalogEntry,
  resolveConsentUrl,
  type CreateProcedureInput,
  type CreatePatientInput,
  type Patient,
  type PatientId,
  type ProcedureCatalogEntry,
  type UserId,
} from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { avatarGradient } from '@/components/ui/avatar';
import { cn } from '@/shared/lib/cn';
import { ageFromBirthdate, initials } from '@/shared/lib/format';
import { usePageTitle } from '@/shared/store/page';
import { ApiClientError } from '@/shared/api/client';
import { usePatientsList, useUpdatePatient } from '@/features/patients/api';
import { PatientForm } from '@/features/patients/PatientForm';
import { useCreateUser, useUsersList } from '@/features/users/api';
import { useCreateProcedure } from './api';

const ROOM_OPTIONS = RoomSchema.options;
const TIPO_OPTIONS = [...PROCEDURES_CATALOG]
  .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

type Feedback =
  | { kind: 'idle' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; message: string };

type ConsentPreview =
  | { kind: 'sin_tipo' }
  | { kind: 'no_aplica'; entry: ProcedureCatalogEntry }
  | { kind: 'medico_no_coincide'; entry: ProcedureCatalogEntry }
  | { kind: 'listo'; entry: ProcedureCatalogEntry; url: string; medicoNombre: string | undefined };

interface FormValues {
  pacienteId: PatientId | '';
  fecha: string;
  hora: string;
  sala: '' | (typeof ROOM_OPTIONS)[number];
  tipo: string;
  tiposAdicionales: string[];
  medicoId: UserId | '';
  anestesiologoId: UserId | '';
  enfermeraId: UserId | '';
  entidad: string;
  observaciones: string;
}

const DEFAULT_VALUES: FormValues = {
  pacienteId: '',
  fecha: '',
  hora: '',
  sala: '',
  tipo: '',
  tiposAdicionales: [],
  medicoId: '',
  anestesiologoId: '',
  enfermeraId: '',
  entidad: '',
  observaciones: '',
};

function toCreateInput(values: FormValues): CreateProcedureInput {
  const base = {
    pacienteId: values.pacienteId as PatientId,
    fecha: values.fecha,
    hora: values.hora,
    tipo: values.tipo,
    medicoId: values.medicoId as UserId,
  };
  const result: CreateProcedureInput = { ...base };
  if (values.sala !== '') result.sala = values.sala;
  if (values.anestesiologoId !== '') result.anestesiologoId = values.anestesiologoId;
  if (values.enfermeraId !== '') result.enfermeraId = values.enfermeraId;
  if (values.entidad.trim() !== '') result.entidad = values.entidad.trim();
  if (values.observaciones.trim() !== '') result.observaciones = values.observaciones.trim();
  if (values.tiposAdicionales.length > 0) {
    result.tiposAdicionales = values.tiposAdicionales;
  }
  return CreateProcedureSchema.parse(result);
}

export function ProcedureNewPage(): JSX.Element {
  usePageTitle('Nuevo procedimiento', 'Crea un episodio clínico — estado inicial: Abierto');
  const navigate = useNavigate();
  const createProcedure = useCreateProcedure();
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'idle' });
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [editPatientOpen, setEditPatientOpen] = useState<boolean>(false);
  const [quickAdd, setQuickAdd] = useState<'medico' | 'anestesiologo' | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    // We keep our own narrow form types and validate the projection with Zod
    // at submit time (see `toCreateInput`). This avoids forcing all selects
    // to start non-empty which would create an unsafe default.
    defaultValues: DEFAULT_VALUES,
  });

  const usersQuery = useUsersList({ page: 1, pageSize: 100 });

  const usersByRole = useMemo(() => {
    const items = usersQuery.data?.items ?? [];
    const filterBy = (...roles: string[]): typeof items =>
      items.filter((u) => roles.includes(u.rol) && u.estado === 'activo');
    return {
      medicos: filterBy('medico', 'admin'),
      anestesiologos: filterBy('anestesiologo'),
      enfermeras: filterBy('enfermera'),
    };
  }, [usersQuery.data]);

  // Auto-load preview: cuando el usuario selecciona tipo (+ médico cuando aplica),
  // mostramos un panel indicando qué consentimiento se cargará al firmar.
  const tipoSeleccionado = watch('tipo');
  const medicoSeleccionadoId = watch('medicoId');

  const preview = useMemo<ConsentPreview>(() => {
    const tipo = tipoSeleccionado;
    if (tipo === undefined || tipo === '') return { kind: 'sin_tipo' };
    const entry = findCatalogEntry(tipo);
    if (entry === undefined) return { kind: 'sin_tipo' };
    if (entry.consentimiento.kind === 'no_aplica') {
      return { kind: 'no_aplica', entry };
    }
    const medico = usersByRole.medicos.find((u) => u.id === medicoSeleccionadoId);
    const url = resolveConsentUrl(tipo, medico?.nombre);
    if (url === null && entry.consentimiento.kind === 'especifico_medico') {
      return { kind: 'medico_no_coincide', entry };
    }
    if (url === null) return { kind: 'sin_tipo' };
    return { kind: 'listo', entry, url, medicoNombre: medico?.nombre };
  }, [tipoSeleccionado, medicoSeleccionadoId, usersByRole.medicos]);

  const handlePatientPick = (patient: Patient): void => {
    setSelectedPatient(patient);
    setValue('pacienteId', patient.id, { shouldValidate: false });
  };

  const handlePatientClear = (): void => {
    setSelectedPatient(null);
    setValue('pacienteId', '', { shouldValidate: false });
  };

  const submit: SubmitHandler<FormValues> = async (values) => {
    setFeedback({ kind: 'idle' });
    try {
      const payload = toCreateInput(values);
      const created = await createProcedure.mutateAsync(payload);
      setFeedback({ kind: 'ok', message: 'Procedimiento creado.' });
      setTimeout(() => navigate(`/procedimientos/${created.id}`), 500);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401 || err.status === 403) {
          setFeedback({
            kind: 'error',
            message: 'No tienes permiso para crear procedimientos.',
          });
          return;
        }
        if (err.status === 422 || err.status === 400) {
          setFeedback({
            kind: 'error',
            message:
              'Datos inválidos: verifica que el paciente y los profesionales seleccionados existan.',
          });
          return;
        }
        if (err.status >= 500) {
          setFeedback({
            kind: 'error',
            message: 'Error del servidor. Inténtalo nuevamente en unos instantes.',
          });
          return;
        }
        setFeedback({ kind: 'error', message: err.message });
        return;
      }
      if (err instanceof Error && err.message.includes('parse')) {
        setFeedback({
          kind: 'error',
          message: 'Completa todos los campos obligatorios antes de guardar.',
        });
        return;
      }
      setFeedback({
        kind: 'error',
        message: 'No fue posible conectar con el servidor.',
      });
    }
  };

  return (
    <div className="max-w-[920px] mx-auto">
      <button
        type="button"
        onClick={() => navigate('/procedimientos')}
        className="flex items-center gap-2 text-sm text-text-soft hover:text-text mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a procedimientos
      </button>

      <form
        onSubmit={(e) => {
          void handleSubmit(submit)(e);
        }}
        noValidate
      >
        {/* Paciente */}
        <Card className="p-6 mb-4">
          <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
            <CardTitle>1. Paciente</CardTitle>
            <CardDescription>
              Busca por nombre, documento o ID. Si el paciente no existe, regístralo primero.
            </CardDescription>
          </CardHeader>

          {selectedPatient !== null ? (
            <SelectedPatientCard
              patient={selectedPatient}
              onChange={handlePatientClear}
              onEdit={() => setEditPatientOpen(true)}
            />
          ) : (
            <PatientPicker onPick={handlePatientPick} />
          )}
          <Controller
            control={control}
            name="pacienteId"
            render={() => <input type="hidden" />}
          />
        </Card>

        {selectedPatient !== null && (
          <PatientEditDialog
            patient={selectedPatient}
            open={editPatientOpen}
            onOpenChange={setEditPatientOpen}
            onSaved={(updated) => setSelectedPatient(updated)}
          />
        )}

        {/* Datos del procedimiento */}
        <Card className="p-6 mb-4">
          <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
            <CardTitle>2. Datos del procedimiento</CardTitle>
            <CardDescription>Fecha, hora y tipo del episodio clínico.</CardDescription>
          </CardHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fecha">
                Fecha <span className="text-danger">*</span>
              </Label>
              <Input id="fecha" type="date" {...register('fecha')} />
              {errors.fecha?.message !== undefined && (
                <p className="text-[11.5px] text-danger">{errors.fecha.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hora">
                Hora <span className="text-danger">*</span>
              </Label>
              <Input id="hora" type="time" {...register('hora')} />
              {errors.hora?.message !== undefined && (
                <p className="text-[11.5px] text-danger">{errors.hora.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="tipo">
                Tipo de procedimiento (CUPS) <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="tipo"
                render={({ field }) => (
                  <SearchableSelect
                    id="tipo"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    options={TIPO_OPTIONS.map((p) => ({
                      value: p.nombre,
                      label: `[${p.cups}] ${p.nombre}`,
                      keywords: p.cups,
                    }))}
                  />
                )}
              />
              {errors.tipo?.message !== undefined && (
                <p className="text-[11.5px] text-danger">{errors.tipo.message}</p>
              )}
            </div>

            {/* Procedimientos adicionales del mismo episodio */}
            <div className="md:col-span-2">
              <Controller
                control={control}
                name="tiposAdicionales"
                render={({ field }) => (
                  <AdditionalProceduresPicker
                    tipoPrincipal={watch('tipo')}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            {/* Preview del consentimiento que se cargará */}
            <div className="md:col-span-2">
              <ConsentPreviewPanel preview={preview} />
            </div>
          </div>
        </Card>

        {/* Profesionales */}
        <Card className="p-6 mb-4">
          <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
            <CardTitle>3. Profesionales</CardTitle>
            <CardDescription>
              El médico tratante es obligatorio. El anestesiólogo puede asignarse luego.
            </CardDescription>
          </CardHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="medicoId">
                  Médico tratante <span className="text-danger">*</span>
                </Label>
                <button
                  type="button"
                  className="text-[11px] text-primary hover:underline"
                  onClick={() => setQuickAdd('medico')}
                >
                  + Agregar médico
                </button>
              </div>
              <Controller
                control={control}
                name="medicoId"
                render={({ field }) => (
                  <SearchableSelect
                    id="medicoId"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    loading={usersQuery.isLoading}
                    options={usersByRole.medicos.map((u) => ({
                      value: u.id,
                      label:
                        u.registroProfesional !== undefined && u.registroProfesional !== ''
                          ? `${u.nombre} — ${u.registroProfesional}`
                          : u.nombre,
                      keywords: u.registroProfesional,
                    }))}
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="anestesiologoId">Anestesiólogo</Label>
                <button
                  type="button"
                  className="text-[11px] text-primary hover:underline"
                  onClick={() => setQuickAdd('anestesiologo')}
                >
                  + Agregar anestesiólogo
                </button>
              </div>
              <Controller
                control={control}
                name="anestesiologoId"
                render={({ field }) => (
                  <SearchableSelect
                    id="anestesiologoId"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    loading={usersQuery.isLoading}
                    placeholder="— Sin asignar —"
                    clearable
                    options={usersByRole.anestesiologos.map((u) => ({
                      value: u.id,
                      label:
                        u.registroProfesional !== undefined && u.registroProfesional !== ''
                          ? `${u.nombre} — ${u.registroProfesional}`
                          : u.nombre,
                      keywords: u.registroProfesional,
                    }))}
                  />
                )}
              />
            </div>

          </div>
        </Card>

        {/* Detalles */}
        <Card className="p-6 mb-4">
          <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
            <CardTitle>4. Detalles administrativos</CardTitle>
            <CardDescription>La entidad responsable es opcional.</CardDescription>
          </CardHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="entidad">Entidad</Label>
              <Controller
                control={control}
                name="entidad"
                render={({ field }) => (
                  <SearchableSelect
                    id="entidad"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                    options={ENTIDADES_CATALOG.map((e) => ({ value: e, label: e }))}
                  />
                )}
              />
            </div>
          </div>
        </Card>

        {feedback.kind === 'error' && (
          <div className="mb-4 px-4 py-3 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200">
            {feedback.message}
          </div>
        )}
        {feedback.kind === 'ok' && (
          <div className="mb-4 px-4 py-3 rounded-md bg-success-soft text-green-800 text-sm border border-green-200">
            {feedback.message}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => navigate('/procedimientos')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting || selectedPatient === null}>
            <Save className="w-4 h-4" />
            {isSubmitting ? 'Guardando…' : 'Guardar procedimiento'}
          </Button>
        </div>
      </form>

      {quickAdd !== null && (
        <QuickAddStaffModal
          rol={quickAdd}
          onClose={() => setQuickAdd(null)}
          onCreated={(u) => {
            setQuickAdd(null);
            if (u.rol === 'medico' || u.rol === 'admin') {
              setValue('medicoId', u.id, { shouldValidate: false });
            } else if (u.rol === 'anestesiologo') {
              setValue('anestesiologoId', u.id, { shouldValidate: false });
            }
          }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Patient picker — search con debounce + cards clickables                    */
/* -------------------------------------------------------------------------- */

interface PatientPickerProps {
  onPick(patient: Patient): void;
}

function PatientPicker({ onPick }: PatientPickerProps): JSX.Element {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => {
      clearTimeout(handle);
    };
  }, [search]);

  const { data, isLoading, isError } = usePatientsList({
    page: 1,
    pageSize: 8,
    q: debouncedSearch === '' ? undefined : debouncedSearch,
  });

  const items = data?.items ?? [];

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <Input
          placeholder="Buscar paciente por nombre, documento o ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {isError && (
        <div className="mt-3 text-sm text-danger">
          No fue posible cargar la lista de pacientes.
        </div>
      )}

      {!isError && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 rounded-md bg-surface-alt animate-pulse" />
            ))
          ) : items.length === 0 ? (
            <div className="md:col-span-2 text-sm text-text-muted py-4 text-center">
              {debouncedSearch === ''
                ? 'Empieza a escribir para buscar pacientes.'
                : 'Sin coincidencias para esa búsqueda.'}
            </div>
          ) : (
            items.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPick(p)}
                className="flex items-center gap-3 p-2.5 rounded-md border border-border bg-surface hover:border-primary hover:bg-primary-soft text-left transition-colors"
              >
                <div
                  className={cn(
                    'w-9 h-9 rounded-md grid place-items-center text-white font-bold text-xs flex-shrink-0',
                    avatarGradient(p.id),
                  )}
                >
                  {initials(p.nombres, p.apellidos)}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-text truncate">
                    {p.nombres} {p.apellidos}
                  </div>
                  <div className="text-text-muted text-xs truncate">
                    {p.tipoDoc} {p.documento} · {p.id}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface SelectedPatientCardProps {
  patient: Patient;
  onChange(): void;
  onEdit(): void;
}

/** Muestra un dato del expediente; si está vacío, un guion tenue. */
function InfoField({ label, value }: { label: string; value?: string }): JSX.Element {
  const empty = value === undefined || value.trim() === '';
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
        {label}
      </div>
      <div className={cn('text-sm truncate', empty ? 'text-text-muted italic' : 'text-text')}>
        {empty ? '— sin registrar —' : value}
      </div>
    </div>
  );
}

function SelectedPatientCard({ patient, onChange, onEdit }: SelectedPatientCardProps): JSX.Element {
  const edad = ageFromBirthdate(patient.fechaNacimiento);
  const sexo = patient.sexo === 'F' ? 'Femenino' : patient.sexo === 'M' ? 'Masculino' : 'Otro';
  return (
    <div className="rounded-md border border-primary/30 bg-primary-soft">
      <div className="flex items-center gap-3 p-3">
        <div
          className={cn(
            'w-11 h-11 rounded-md grid place-items-center text-white font-bold flex-shrink-0',
            avatarGradient(patient.id),
          )}
        >
          {initials(patient.nombres, patient.apellidos)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-text flex items-center gap-2 flex-wrap">
            <UserIcon className="w-4 h-4 text-primary" />
            {patient.nombres} {patient.apellidos}
          </div>
          <div className="text-text-muted text-xs">
            {patient.tipoDoc} {patient.documento} · {edad} años · {sexo} ·{' '}
            <span className="font-mono">{patient.id}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button type="button" variant="secondary" size="sm" onClick={onEdit}>
            <Pencil className="w-3.5 h-3.5" /> Editar datos
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={onChange}>
            Cambiar
          </Button>
        </div>
      </div>

      {/* Toda la info del expediente, visible al seleccionar */}
      <div className="border-t border-primary/20 px-3 py-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 bg-surface/40 rounded-b-md">
        <InfoField label="Fecha de nacimiento" value={patient.fechaNacimiento} />
        <InfoField label="Teléfono" value={patient.telefono} />
        <InfoField label="Entidad / EPS" value={patient.eps} />
        <InfoField label="Ciudad" value={patient.ciudad} />
        <InfoField label="Contacto de emergencia" value={patient.contactoEmergencia} />
        <div className="col-span-2 md:col-span-3">
          <InfoField label="Dirección" value={patient.direccion} />
        </div>
        <div className="col-span-2 md:col-span-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
            Alergias
          </div>
          <div
            className={cn(
              'text-sm',
              patient.alergias !== undefined && patient.alergias.trim() !== ''
                ? 'text-danger font-medium'
                : 'text-text-muted italic',
            )}
          >
            {patient.alergias !== undefined && patient.alergias.trim() !== ''
              ? patient.alergias
              : '— sin registrar —'}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Diálogo de edición del paciente (reutiliza PatientForm; persiste en el      */
/* expediente para próximos procedimientos)                                    */
/* -------------------------------------------------------------------------- */

interface PatientEditDialogProps {
  patient: Patient;
  open: boolean;
  onOpenChange(open: boolean): void;
  onSaved(patient: Patient): void;
}

function patientToFormDefaults(p: Patient): CreatePatientInput {
  return {
    tipoDoc: p.tipoDoc,
    documento: p.documento,
    nombres: p.nombres,
    apellidos: p.apellidos,
    fechaNacimiento: p.fechaNacimiento,
    sexo: p.sexo,
    telefono: p.telefono ?? '',
    direccion: p.direccion ?? '',
    ciudad: p.ciudad ?? 'Barranquilla',
    eps: p.eps ?? '',
    historiaClinica: p.historiaClinica ?? '',
    alergias: p.alergias ?? '',
    contactoEmergencia: p.contactoEmergencia ?? '',
  };
}

function PatientEditDialog({
  patient,
  open,
  onOpenChange,
  onSaved,
}: PatientEditDialogProps): JSX.Element {
  const updatePatient = useUpdatePatient();
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'idle' });

  const handleSubmit = async (values: CreatePatientInput): Promise<void> => {
    setFeedback({ kind: 'idle' });
    try {
      const updated = await updatePatient.mutateAsync({ id: patient.id, patch: values });
      onSaved(updated);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401 || err.status === 403) {
          setFeedback({ kind: 'error', message: 'No tienes permiso para editar pacientes.' });
          return;
        }
        if (err.status === 409) {
          setFeedback({
            kind: 'error',
            message: 'Conflicto: el documento ya está en uso por otro paciente.',
          });
          return;
        }
        if (err.status >= 400 && err.status < 500) {
          setFeedback({ kind: 'error', message: 'Datos inválidos. Revisa los campos resaltados.' });
          return;
        }
      }
      setFeedback({ kind: 'error', message: 'No fue posible guardar. Inténtalo de nuevo.' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar datos del paciente</DialogTitle>
          <DialogDescription>
            Los cambios se guardan en el expediente de {patient.nombres} {patient.apellidos} y se
            reutilizan en los próximos procedimientos.
          </DialogDescription>
        </DialogHeader>
        <PatientForm
          defaultValues={patientToFormDefaults(patient)}
          submitLabel="Guardar cambios"
          submittingLabel="Guardando…"
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          feedback={feedback}
          lockIdentification
        />
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Panel de preview del consentimiento que se cargará al firmar               */
/* -------------------------------------------------------------------------- */

interface ConsentPreviewPanelProps {
  preview: ConsentPreview;
}

function ConsentPreviewPanel({ preview }: ConsentPreviewPanelProps): JSX.Element {
  if (preview.kind === 'sin_tipo') {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-xs text-text-muted">
        Selecciona un tipo de procedimiento para ver qué consentimiento se cargará automáticamente.
      </div>
    );
  }

  if (preview.kind === 'no_aplica') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-border bg-surface-alt p-3">
        <FileWarning className="w-4 h-4 text-text-muted mt-0.5 flex-shrink-0" />
        <div className="text-xs">
          <div className="font-semibold text-text">
            CUPS {preview.entry.cups} — no requiere consentimiento médico
          </div>
          <div className="text-text-muted mt-0.5">
            Este procedimiento es una consulta o test que no requiere consentimiento firmado por el
            paciente. Solo se generarán los demás documentos del episodio.
          </div>
        </div>
      </div>
    );
  }

  if (preview.kind === 'medico_no_coincide') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-warning bg-warning-soft p-3">
        <FileWarning className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
        <div className="text-xs">
          <div className="font-semibold text-text">
            CUPS {preview.entry.cups} — requiere un médico específico
          </div>
          <div className="text-text-muted mt-0.5">
            Selecciona el médico tratante apropiado para que se cargue el consentimiento correcto.
          </div>
        </div>
      </div>
    );
  }

  // preview.kind === 'listo'
  const archivoNombre = decodeURIComponent(preview.url.replace(/^\/consents\//, ''));
  return (
    <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary-soft p-3">
      <FileCheck2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
      <div className="text-xs flex-1 min-w-0">
        <div className="font-semibold text-text">
          CUPS {preview.entry.cups} — consentimiento listo
        </div>
        <div className="text-text-soft mt-0.5 truncate" title={archivoNombre}>
          Se cargará: <span className="font-mono">{archivoNombre}</span>
        </div>
        {preview.medicoNombre !== undefined && (
          <div className="text-text-muted mt-0.5">
            Versión específica para: {preview.medicoNombre}
          </div>
        )}
        <a
          href={preview.url}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-1 text-primary hover:underline"
        >
          Ver consentimiento ↗
        </a>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Modal — Agregar médico / anestesiólogo en línea                            */
/* -------------------------------------------------------------------------- */

interface QuickAddStaffModalProps {
  rol: 'medico' | 'anestesiologo';
  onClose(): void;
  onCreated(user: { id: UserId; rol: string }): void;
}

function QuickAddStaffModal({ rol, onClose, onCreated }: QuickAddStaffModalProps): JSX.Element {
  const createUser = useCreateUser();
  const [nombre, setNombre] = useState<string>('');
  const [correo, setCorreo] = useState<string>('');
  const [registroProfesional, setRm] = useState<string>('');
  const [especialidad, setEspecialidad] = useState<string>(
    rol === 'medico' ? 'Gastroenterólogo' : '',
  );
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const titulo = rol === 'medico' ? 'Agregar nuevo médico' : 'Agregar nuevo anestesiólogo';

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (nombre.trim() === '' || correo.trim() === '' || registroProfesional.trim() === '') {
      setError('Nombre, correo y RM son obligatorios.');
      return;
    }
    try {
      const created = await createUser.mutateAsync({
        nombre: nombre.trim(),
        correo: correo.trim(),
        rol,
        estado: 'activo',
        especialidad: especialidad.trim() === '' ? undefined : especialidad.trim(),
        registroProfesional: registroProfesional.trim(),
        password,
      });
      onCreated({ id: created.id, rol: created.rol });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('No fue posible crear el usuario.');
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-bold text-text">{titulo}</h3>
          <button
            type="button"
            className="text-text-muted hover:text-text text-xl leading-none"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="p-5 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="qa-nombre">
              Nombre completo <span className="text-danger">*</span>
            </Label>
            <Input
              id="qa-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Dra. Carolina Mejía Pérez"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="qa-rm">
              Registro médico (RM) <span className="text-danger">*</span>
            </Label>
            <Input
              id="qa-rm"
              value={registroProfesional}
              onChange={(e) => setRm(e.target.value)}
              placeholder="R.M. 12345678"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="qa-esp">Especialidad</Label>
            <Input
              id="qa-esp"
              value={especialidad}
              onChange={(e) => setEspecialidad(e.target.value)}
              placeholder={rol === 'medico' ? 'Gastroenterólogo' : 'Anestesiólogo'}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="qa-correo">
              Correo institucional <span className="text-danger">*</span>
            </Label>
            <Input
              id="qa-correo"
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="nombre.apellido@endomedical.co"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="qa-pwd">Password temporal</Label>
            <Input
              id="qa-pwd"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 12 caract., mayús, minús, dígito y especial"
            />
            <p className="text-[11px] text-text-muted">
              El usuario deberá cambiarla en el primer inicio de sesión.
            </p>
          </div>
          {error !== null && (
            <div className="rounded-md bg-danger-soft border border-red-200 text-red-800 text-xs px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending ? 'Creando…' : 'Crear y seleccionar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* AdditionalProceduresPicker — multi-add para episodios con 2+ CUPS          */
/* -------------------------------------------------------------------------- */

interface AdditionalProceduresPickerProps {
  tipoPrincipal: string;
  value: string[];
  onChange(next: string[]): void;
}

function AdditionalProceduresPicker({
  tipoPrincipal,
  value,
  onChange,
}: AdditionalProceduresPickerProps): JSX.Element {
  const [pendingType, setPendingType] = useState<string>('');
  const allOptions = TIPO_OPTIONS;
  // Excluimos el principal y los ya agregados del dropdown.
  const availables = allOptions.filter(
    (p) => p.nombre !== tipoPrincipal && !value.includes(p.nombre),
  );

  const add = (): void => {
    if (pendingType === '') return;
    onChange([...value, pendingType]);
    setPendingType('');
  };
  const remove = (t: string): void => {
    onChange(value.filter((v) => v !== t));
  };

  return (
    <div className="rounded-md border border-dashed border-border p-3 bg-surface-alt/50 w-full min-w-0">
      <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider font-bold text-text-muted">
            Procedimientos adicionales del episodio
          </div>
          <div className="text-[11px] text-text-muted mt-0.5">
            Opcional. El paciente puede recibir 2+ procedimientos en la misma sesión —
            cada uno aporta su propio consentimiento y suma para el conteo de payroll.
          </div>
        </div>
        <Badge variant="indigo" className="flex-shrink-0">
          {1 + value.length} procedimiento{1 + value.length === 1 ? '' : 's'} en total
        </Badge>
      </div>

      {value.length > 0 && (
        <div className="flex flex-col gap-1.5 mb-2">
          {value.map((t) => {
            const entry = allOptions.find((p) => p.nombre === t);
            return (
              <div
                key={t}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border bg-surface text-[12.5px]"
              >
                <span className="font-mono text-[10px] text-text-muted">
                  {entry !== undefined ? `[${entry.cups}]` : ''}
                </span>
                <span className="flex-1 truncate">{t}</span>
                <button
                  type="button"
                  onClick={() => remove(t)}
                  className="text-danger hover:underline text-[11px] font-semibold"
                >
                  Quitar
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 items-stretch w-full min-w-0">
        <div className="flex-1 min-w-0">
          <SearchableSelect
            value={pendingType}
            onChange={setPendingType}
            disabled={availables.length === 0 || tipoPrincipal === ''}
            placeholder={
              tipoPrincipal === ''
                ? 'Selecciona primero el tipo principal'
                : availables.length === 0
                  ? 'No quedan procedimientos disponibles'
                  : '— Selecciona un procedimiento adicional —'
            }
            options={availables.map((p) => ({
              value: p.nombre,
              label: `[${p.cups}] ${p.nombre}`,
              keywords: p.cups,
            }))}
          />
        </div>
        <button
          type="button"
          onClick={add}
          disabled={pendingType === ''}
          className="flex-shrink-0 px-3 rounded-md bg-primary text-white text-sm font-semibold disabled:opacity-50 hover:bg-primary-dark whitespace-nowrap"
        >
          + Agregar
        </button>
      </div>
    </div>
  );
}
